import React, { useState } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import {
  FormControlLabel,
  Stack,
  Switch,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { PercentOutlined } from '@mui/icons-material';
import { DiscountTypes } from 'api';
import { FormCard, FormInput } from 'components';
import DISCOUNTS from 'constants/discounts';
import { arrayToOptions } from 'helpers/common';

interface Props {
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
}

const PREFIXES = [
  'off',
  'omg',
  'yas',
  'fal',
  'hap',
  'cal',
  'cbg',
  'cba',
  'sno',
];

const options = arrayToOptions(PREFIXES);

const PrefixDiscount: React.FC<Props> = (props: Props): JSX.Element => {
  const { control, setValue } = props;

  const [enableShippingDiscount, setEnableShippingDiscount] = useState(false);

  const handleEnableShippingChange = () => {
    setEnableShippingDiscount((p) => !p);
  };

  return (
    <FormCard title="Settings">
      <Stack spacing={2}>
        <FormInput
          control={control}
          inputType="autocompleteCreatable"
          name="metafields_attributes.0.value.prefix"
          label="Prefix logic"
          placeholder="Select or create option"
          options={options}
          rules={{
            required: true,
            onChange: (e) => {
              setValue('coupon_affix', e.target.value);
            },
          }}
        />
        <FormInput
          control={control}
          name="metafields_attributes.0.value.mix_and_match"
          inputType="switch"
          label="Mix and match"
          onChange={(e) => {
            const target = e.target as HTMLInputElement;
            const { checked } = target;
            setValue('metafields_attributes.0.value.mix_and_match', checked);
            setValue('metafields_attributes.0.value.mixmatch', checked);
          }}
        />

        <FormInput
          control={control}
          name="metafields_attributes.0.value.basis"
          inputType="radio"
          label="Discount calculation"
          options={DISCOUNTS.BASIS_OPTIONS}
          helperText="Discounted price will be calculated based on the selected option"
        />

        <FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={enableShippingDiscount}
                onChange={handleEnableShippingChange}
                name="enableShippingDiscount"
                color="primary"
              />
            }
            label="Enable shipping discount"
          />
          <FormHelperText>
            Use this when the same discount should also apply to delivery
            options except the excluded titles.
          </FormHelperText>
        </FormControl>

        {enableShippingDiscount && (
          <>
            <FormInput
              control={control}
              name="metafields_attributes.0.value.title"
              inputType="input"
              label="Excluded delivery option titles"
              helperText="Enter one title or multiple titles separated by commas. Matching delivery options will be excluded from the shipping discount."
            />

            <FormInput
              control={control}
              name="metafields_attributes.0.value.percentOffOnShipping"
              inputType="number"
              label="Shipping discount percentage"
              InputProps={{
                endAdornment: <PercentOutlined fontSize="small" />,
                inputProps: {
                  min: 0,
                  max: 100,
                },
              }}
              rules={{
                min: 0,
                max: 100,
              }}
            />

            <FormInput
              control={control}
              name="metafields_attributes.0.value.minimumSubtotal"
              inputType="number"
              label="Minimum subtotal"
              InputProps={{
                inputProps: {
                  min: 0,
                },
              }}
              rules={{
                min: 0,
              }}
            />
          </>
        )}
      </Stack>
    </FormCard>
  );
};

export default PrefixDiscount;
